import type {
  VendorRecord,
  CreateVendorRequest,
  CreateVendorResponse,
  DeleteVendorResponse,
  UpdateVendorRequest,
  UpdateVendorResponse,
  VendorDiscoveredModelRecord,
  VendorOfficialKeyResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  createVendor,
  deleteVendor,
  discoverVendorModels,
  getVendorOfficialKey,
  listVendorDiscoveredModels,
  listVendors,
  setVendorOfficialKey,
  updateVendor,
} from './vendors.ts';
import { MODELS_QUERY_KEY } from './useModels.ts';

const VENDORS_KEY = ['vendors'] as const;

export function useVendorsList(): UseQueryResult<readonly VendorRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...VENDORS_KEY],
    queryFn: async () => {
      const res = await listVendors(fetchImpl);
      return res.vendors;
    },
  });
}

export function useCreateVendor(): UseMutationResult<CreateVendorResponse, Error, CreateVendorRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVendorRequest) => createVendor(body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: VENDORS_KEY }); },
  });
}

export function useUpdateVendor(): UseMutationResult<UpdateVendorResponse, Error, { slug: string; body: UpdateVendorRequest }> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }) => updateVendor(slug, body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: VENDORS_KEY }); },
  });
}

export function useDeleteVendor(): UseMutationResult<DeleteVendorResponse, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => deleteVendor(slug, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: VENDORS_KEY }); },
  });
}

export function vendorOfficialKeyQueryKey(slug: string): readonly ['vendors', string, 'official-key'] {
  return ['vendors', slug, 'official-key'] as const;
}

export function vendorDiscoveredModelsQueryKey(slug: string): readonly ['vendors', string, 'discovered-models'] {
  return ['vendors', slug, 'discovered-models'] as const;
}

export function useVendorOfficialKey(
  slug: string | null,
): UseQueryResult<VendorOfficialKeyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: vendorOfficialKeyQueryKey(slug ?? ''),
    queryFn: () => {
      if (slug === null || slug.length === 0) {
        throw new Error('useVendorOfficialKey: slug is required');
      }
      return getVendorOfficialKey(slug, fetchImpl);
    },
    enabled: slug !== null && slug.length > 0,
  });
}

export function useVendorDiscoveredModels(
  slug: string | null,
): UseQueryResult<readonly VendorDiscoveredModelRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: vendorDiscoveredModelsQueryKey(slug ?? ''),
    queryFn: async () => {
      if (slug === null || slug.length === 0) {
        throw new Error('useVendorDiscoveredModels: slug is required');
      }
      const res = await listVendorDiscoveredModels(slug, fetchImpl);
      return res.models;
    },
    enabled: slug !== null && slug.length > 0,
  });
}

export function useSetVendorOfficialKey(): UseMutationResult<
  VendorOfficialKeyResponse,
  Error,
  { slug: string; api_key: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, api_key }) => setVendorOfficialKey(slug, { api_key }, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: vendorOfficialKeyQueryKey(vars.slug) });
    },
  });
}

export function useDiscoverVendorModels(): UseMutationResult<
  { seen: number; missing: number },
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug) => {
      const res = await discoverVendorModels(slug, fetchImpl);
      return { seen: res.seen, missing: res.missing };
    },
    onSuccess: (_data, slug) => {
      void qc.invalidateQueries({ queryKey: vendorDiscoveredModelsQueryKey(slug) });
      void qc.invalidateQueries({ queryKey: vendorOfficialKeyQueryKey(slug) });
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
    },
  });
}
